function initCasekooSlider(root) {
  if (root.dataset.ckReady === 'true') return;
  root.dataset.ckReady = 'true';

  const slides = [...root.querySelectorAll('[data-ck-slide]')];
  const dots = [...root.querySelectorAll('[data-ck-dot]')];
  const previous = root.querySelector('[data-ck-previous]');
  const next = root.querySelector('[data-ck-next]');
  const autoplaySeconds = Number(root.dataset.autoplay || 0);
  let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
  let timer;

  const show = (index) => {
    activeIndex = (index + slides.length) % slides.length;
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
    });
    dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === activeIndex;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  };

  const stop = () => window.clearInterval(timer);
  const start = () => {
    stop();
    if (autoplaySeconds > 0 && slides.length > 1) {
      timer = window.setInterval(() => show(activeIndex + 1), autoplaySeconds * 1000);
    }
  };

  previous?.addEventListener('click', () => {
    show(activeIndex - 1);
    start();
  });
  next?.addEventListener('click', () => {
    show(activeIndex + 1);
    start();
  });
  dots.forEach((dot, dotIndex) => {
    dot.addEventListener('click', () => {
      show(dotIndex);
      start();
    });
  });
  root.addEventListener('mouseenter', stop);
  root.addEventListener('mouseleave', start);
  root.addEventListener('focusin', stop);
  root.addEventListener('focusout', start);
  show(activeIndex);
  start();
}

function moveCasekooScrollTrack(root, direction) {
  const track = root.querySelector('[data-ck-scroll-track]');
  if (!track) return;

  const card = [...track.children].find((child) => child instanceof HTMLElement && !child.hidden);
  const computedStyle = window.getComputedStyle(track);
  let gap = Number.parseFloat(computedStyle.columnGap || computedStyle.gap || '0');
  if (!Number.isFinite(gap)) gap = 0;
  const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;

  track.scrollTo({
    left: Math.max(0, track.scrollLeft + direction * distance),
    behavior: 'smooth',
  });
  window.setTimeout(() => root.dispatchEvent(new CustomEvent('ck:scroll:update')), 360);
}

function initCasekooScrollControlFallback() {
  window.casekooScrollControl = (control, direction) => {
    const root = control.closest('[data-ck-scroll]');
    if (!root) return;
    moveCasekooScrollTrack(root, direction);
  };

  if (document.documentElement.dataset.ckScrollFallbackReady === 'true') return;
  document.documentElement.dataset.ckScrollFallbackReady = 'true';

  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-ck-scroll-previous], [data-ck-scroll-next]');
    if (!control || control.disabled) return;
    if (control.dataset.ckDirectScroll === 'true') return;

    const root = control.closest('[data-ck-scroll]');
    if (!root) return;

    const direction = control.matches('[data-ck-scroll-previous]') ? -1 : 1;
    moveCasekooScrollTrack(root, direction);
  });
}

function initCasekooScroll(root) {
  if (root.dataset.ckScrollReady === 'true') return;
  root.dataset.ckScrollReady = 'true';
  const track = root.querySelector('[data-ck-scroll-track]');
  const previous = root.querySelector('[data-ck-scroll-previous]');
  const next = root.querySelector('[data-ck-scroll-next]');
  if (!track) return;
  const autoScrollDirection = Number(root.dataset.ckAutoScroll || 0);
  let autoScrollFrame;
  let autoScrollLastFrame;

  const updateControls = () => {
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (previous) previous.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= maxScroll - 1;
  };

  const stopAutoScroll = () => {
    window.cancelAnimationFrame(autoScrollFrame);
    autoScrollFrame = undefined;
    autoScrollLastFrame = undefined;
  };

  const runAutoScroll = (timestamp) => {
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

    if (maxScroll > 1) {
      if (!autoScrollLastFrame) autoScrollLastFrame = timestamp;
      const elapsed = timestamp - autoScrollLastFrame;
      autoScrollLastFrame = timestamp;
      const distance = (elapsed / 1000) * 24 * autoScrollDirection;
      let nextLeft = track.scrollLeft + distance;

      if (nextLeft >= maxScroll - 1) {
        nextLeft = 0;
      } else if (nextLeft <= 1 && autoScrollDirection < 0) {
        nextLeft = maxScroll;
      }

      track.scrollLeft = nextLeft;
    }

    autoScrollFrame = window.requestAnimationFrame(runAutoScroll);
  };

  const startAutoScroll = () => {
    stopAutoScroll();
    if (autoScrollDirection !== 0) {
      autoScrollFrame = window.requestAnimationFrame(runAutoScroll);
    }
  };

  const move = (direction) => {
    stopAutoScroll();
    moveCasekooScrollTrack(root, direction);
    window.setTimeout(updateControls, 360);
    window.setTimeout(startAutoScroll, 900);
  };

  previous?.addEventListener('click', (event) => {
    event.stopPropagation();
    if (event.currentTarget.dataset.ckDirectScroll === 'true') return;
    move(-1);
  });
  next?.addEventListener('click', (event) => {
    event.stopPropagation();
    if (event.currentTarget.dataset.ckDirectScroll === 'true') return;
    move(1);
  });
  track.addEventListener('scroll', updateControls);
  root.addEventListener('ck:scroll:update', updateControls);
  root.addEventListener('mouseenter', stopAutoScroll);
  root.addEventListener('mouseleave', startAutoScroll);
  root.addEventListener('focusin', stopAutoScroll);
  root.addEventListener('focusout', startAutoScroll);
  updateControls();
  startAutoScroll();
}

function initCasekooTabs(root) {
  if (root.dataset.ckTabsReady === 'true') return;
  root.dataset.ckTabsReady = 'true';
  const tabs = [...root.querySelectorAll('[data-ck-tab]')];
  const cards = [...root.querySelectorAll('[data-ck-category]')];

  const showCategory = (category) => {
    tabs.forEach((tab) => {
      const isActive = tab.dataset.ckTab === category;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });
    cards.forEach((card) => {
      card.hidden = card.dataset.ckCategory !== category;
    });
    root.querySelector('[data-ck-scroll-track]')?.scrollTo({ left: 0, behavior: 'auto' });
    root.dispatchEvent(new CustomEvent('ck:scroll:update'));
  };

  tabs.forEach((tab) => tab.addEventListener('click', () => showCategory(tab.dataset.ckTab)));
  if (tabs[0]) showCategory(tabs[0].dataset.ckTab);
}

function initCasekooSections(scope = document) {
  initCasekooScrollControlFallback();
  scope.querySelectorAll('[data-ck-slider]').forEach(initCasekooSlider);
  scope.querySelectorAll('[data-ck-scroll]').forEach(initCasekooScroll);
  scope.querySelectorAll('[data-ck-tabs]').forEach(initCasekooTabs);
  processCasekooEmbeds(scope);
}

function processCasekooEmbeds(scope = document) {
  if (!scope.querySelector('.instagram-media')) return;

  const process = () => window.instgrm?.Embeds?.process?.();

  if (window.instgrm?.Embeds?.process) {
    process();
    return;
  }

  if (!document.querySelector('script[src*="instagram.com/embed.js"]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.instagram.com/embed.js';
    script.addEventListener('load', process);
    document.head.appendChild(script);
  }
}

document.addEventListener('DOMContentLoaded', () => initCasekooSections());
document.addEventListener('shopify:section:load', (event) => initCasekooSections(event.target));
